import React, { useState, useEffect } from 'react';
import { Plus, Camera } from 'lucide-react';
import ModifyBookingService from './ModifyBookingService';
import { BookingService } from '../../lib/bookings';
import { useBusiness } from '../../contexts/BusinessContext'; 
import { StockItem } from '../../lib/stockItems';
import { supabase } from '../../lib/supabase';
import { getCustomer } from '../../lib/customers';
import CreateVehicleModal from '../CreateVehicleModal';
import SelectVehicleModal from '../manage/SelectVehicleModal';
import SelectServiceModal from '../manage/SelectServiceModal';
import InspectionPhotosModal from './InspectionPhotosModal';
import { CustomerVehicle } from '../../lib/customers';

interface BookingServiceListProps {
  services: BookingService[];
  onAddService?: (vehicleId: string) => void;
  onEditVehicle?: (vehicleId: string) => void;
  onUpdate: () => Promise<void>;
  onError: (error: string) => void;
  customer?: {
    id: string;
    vehicles?: any[];
  };
  showSalesInfo?: boolean;
  canEdit?: boolean;
}

export default function BookingServiceList({
  services,
  onAddService,
  onEditVehicle,
  onUpdate,
  onError,
  customer,
  showSalesInfo = true,
  canEdit = true
}: BookingServiceListProps) {
  const { selectedBusiness } = useBusiness();
  const [isSelectVehicleModalOpen, setIsSelectVehicleModalOpen] = useState(false);
  const [isSelectServiceModalOpen, setIsSelectServiceModalOpen] = useState(false);
  const [isCreateVehicleModalOpen, setIsCreateVehicleModalOpen] = useState(false);
  const [isInspectionPhotosModalOpen, setIsInspectionPhotosModalOpen] = useState(false);
  const [currentVehicleId, setCurrentVehicleId] = useState<string | undefined>(undefined);
  const [currentBookingId, setCurrentBookingId] = useState<string | undefined>(undefined);
  const [photoCountByVehicle, setPhotoCountByVehicle] = useState<Record<string, number>>({});
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | undefined>(undefined);
  const [selectedBookingId, setSelectedBookingId] = useState<string | undefined>(undefined);
  const [showInspectionPhotosModal, setShowInspectionPhotosModal] = useState(false);

  // Group services by vehicle
  const servicesByVehicle = services.reduce<Record<string, BookingService[]>>((acc, service) => {
    const vehicleId = service.vehicle_id;
    if (!acc[vehicleId]) {
      acc[vehicleId] = [];
    }
    acc[vehicleId].push(service);
    return acc;
  }, {});

  useEffect(() => {
    loadPhotoCountsForVehicles();
  }, [services]);

  const loadPhotoCountsForVehicles = async () => {
    if (!supabase) return;

    try {
      const counts: Record<string, number> = {};
      
      for (const service of services) {
        const { data, error } = await supabase.storage
          .from('inspection-photos')
          .list(`${service.booking_id}/${service.vehicle_id}`);

        if (error) throw error;
        counts[service.vehicle_id] = data?.length || 0;
      }

      setPhotoCountByVehicle(counts);
    } catch (err) {
      console.error('Error loading photo counts:', err);
    }
  };

  const handleVehicleSelected = (vehicle: CustomerVehicle) => {
    setIsSelectVehicleModalOpen(false);
    setCurrentVehicleId(undefined);
    setCurrentBookingId(undefined);
    if (onAddService) {
      onAddService(vehicle.id);
    }
  };

  const handleVehicleCreated = async (vehicle: any) => {
    // Update customer data to get the new vehicle
    const updatedCustomer = await getCustomer(customer?.id || '');
    
    // Set the new vehicle as current and open service selection
    setCurrentVehicleId(vehicle.id);
    setIsCreateVehicleModalOpen(false);
    setIsSelectServiceModalOpen(true);
  };

  // Update the photo count after modal closes
  const handleInspectionPhotosClose = () => {
    setIsInspectionPhotosModalOpen(false);
    setCurrentVehicleId(undefined);
    setCurrentBookingId(undefined);
    loadPhotoCountsForVehicles();
  };

  const handleInspectionPhotosClick = (vehicleId: string | null | undefined, bookingId: string | null | undefined) => {
    setSelectedVehicleId(vehicleId === null ? undefined : vehicleId);
    setSelectedBookingId(bookingId === null ? undefined : bookingId);
    setShowInspectionPhotosModal(true);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <h3 className="text-sm font-medium text-gray-900">Services</h3>
          <span className="text-xs text-gray-500">
            ({services?.length || 0})
          </span>
        </div>
      </div>
      {Object.entries(servicesByVehicle).map(([vehicleId, vehicleServices]) => {
        const vehicle = vehicleServices[0];
        const photoCount = photoCountByVehicle[vehicleId] || 0;
        
        return (
          <div key={vehicleId}>
            <div className="bg-gray-50 p-4 border-b border-gray-200">
              <div className="relative">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="text-sm font-medium text-gray-900">
                      {vehicle.vehicle_year} {vehicle.vehicle_make} {vehicle.vehicle_model}
                    </div>
                    <button
                      onClick={() => {
                        setCurrentVehicleId(vehicleId);
                        setCurrentBookingId(vehicle.booking_id);
                        setIsInspectionPhotosModalOpen(true);
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                    >
                      <Camera className="h-3.5 w-3.5 mr-1" />
                      {photoCount > 0 ? (
                        <>
                          Photos
                          <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded-full">
                            {photoCount}
                          </span>
                        </>
                      ) : (
                        'Add Photos'
                      )}
                    </button>
                  </div>
                  {canEdit && onEditVehicle && (
                    <button
                      onClick={() => onEditVehicle(vehicleId)}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Edit
                    </button>
                  )}
                </div>
                {vehicle.vehicle_color && (
                  <div className="text-sm text-gray-500">{vehicle.vehicle_color}</div>
                )}
              </div>
            </div>

            {vehicleServices.map((service) => (
              <div key={service.id} className="p-4 border-b border-gray-200">
                <ModifyBookingService
                  service={service}
                  bookingId={service.booking_id}
                  onUpdate={onUpdate}
                  onError={onError}
                  showSalesInfo={showSalesInfo}
                  canEdit={canEdit}
                />
              </div>
            ))}
            
            {canEdit && (
              <div className="p-4 border-b border-gray-200 flex space-x-4">
                <button
                  onClick={() => {
                    setCurrentVehicleId(vehicleId);
                    setIsSelectServiceModalOpen(true);
                  }}
                  className="flex-1 flex items-center justify-center px-4 py-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Select Service
                </button>
              </div>
            )}
          </div>
        );
      })}

      {canEdit && (
        <div className="p-4">
          <button
            onClick={() => setIsSelectVehicleModalOpen(true)}
            className="w-full flex items-center justify-center px-4 py-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Vehicle
          </button>
        </div>
      )}

      <SelectVehicleModal
        isOpen={isSelectVehicleModalOpen}
        onClose={() => setIsSelectVehicleModalOpen(false)}
        onSelect={handleVehicleSelected}
        vehicles={customer?.vehicles || []}
        selectedVehicles={services.map(s => ({
          id: s.vehicle_id,
          customer_id: customer?.id || '',
          make: s.vehicle_make || '',
          model: s.vehicle_model || '',
          year: s.vehicle_year || new Date().getFullYear(),
          vin: '',
          color: '',
          license_plate: '',
          mileage: 0,
          notes: '',
          is_primary: false,
          last_service: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          deleted_at: null
        }))}
        onAddVehicle={() => {
          setIsSelectVehicleModalOpen(false);
          setCurrentVehicleId(undefined);
          setIsCreateVehicleModalOpen(true);
        }}
      />

      <CreateVehicleModal
        isOpen={isCreateVehicleModalOpen}
        onClose={() => setIsCreateVehicleModalOpen(false)}
        onSuccess={handleVehicleCreated}
        customerId={customer?.id || ''}
      />

      <SelectServiceModal
        isOpen={isSelectServiceModalOpen}
        onClose={() => setIsSelectServiceModalOpen(false)}
        onAdd={async (service, priceOption) => {
          try {
            if (!currentVehicleId) {
              throw new Error('No vehicle selected');
            }

            // Calculate tax amounts with proper currency rounding
            const taxRate = service.tax_exempt ? 0 : 5; // Default tax rate
            const taxAmount = service.tax_exempt ? 0 : Math.round((priceOption.price * taxRate / 100) * 100) / 100;
            const totalAmount = Math.round((priceOption.price + taxAmount) * 100) / 100;

            // Insert new service
            const { error: serviceError } = await supabase!
              .from('booking_services')
              .insert({
                business_id: selectedBusiness?.id,
                booking_id: services[0]?.booking_id,
                service_id: service.id,
                vehicle_id: currentVehicleId,
                name: service.name,
                description: service.description || '',
                price: priceOption.price,
                tax_exempt: service.tax_exempt || false,
                tax_rate: taxRate,
                tax_amount: taxAmount,
                total_amount: totalAmount,
                duration_minutes: priceOption.duration_minutes,
                status: 'pending'
              });

            if (serviceError) throw serviceError;

            // Refresh the booking data
            await onUpdate();

            // Close modal
            setIsSelectServiceModalOpen(false);
            setCurrentVehicleId(undefined);
          } catch (err) {
            console.error('Error adding service:', err);
            onError(err instanceof Error ? err.message : 'Failed to add service');
            return;
          }
        }}
        bookingId={services[0]?.booking_id}
        vehicleId={currentVehicleId}
      />

      <InspectionPhotosModal
        isOpen={isInspectionPhotosModalOpen}
        onClose={handleInspectionPhotosClose}
        bookingId={currentBookingId || ''}
        vehicleId={currentVehicleId || ''}
        onUpdate={onUpdate}
        onError={onError}
      />
    </div>
  );
}